import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  computed,
  effect,
  inject,
  viewChild
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { AnalyticsService } from '@core/services/infra/analytics.service';
import { environment } from '../../../../environments/environment';

/**
 * Bottom-anchored cookie-consent banner for the app surface (app.propor.io).
 *
 * Only renders when analytics is enabled for the build (prod) AND the visitor
 * has not yet chosen. Accepting/declining flips Google Consent Mode v2 via
 * {@link AnalyticsService}. Mirrors the landing site's banner so the experience
 * stays consistent across propor.io and app.propor.io.
 */
@Component({
  selector: 'app-cookie-consent-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, TranslatePipe],
  template: `
    @if (show()) {
      <div
        #banner
        class="cc-banner"
        role="dialog"
        aria-live="polite"
        [attr.aria-label]="'shared.cookieConsent.title' | translate"
      >
        <div class="cc-inner">
          <div class="cc-text">
            <strong>{{ 'shared.cookieConsent.title' | translate }}</strong>
            <span>
              {{ 'shared.cookieConsent.message' | translate }}
              @if (privacyUrl) {
                <a [href]="privacyUrl" target="_blank" rel="noopener noreferrer">{{
                  'shared.cookieConsent.privacy' | translate
                }}</a>
              }
            </span>
          </div>
          <!--
            Both actions are mat-flat-button of the same size. The Belgian DPA
            (APD/GBA) requires refusing to be as easy and as visible as
            accepting; a low-emphasis "reject" next to a filled "accept" is the
            dark pattern it has repeatedly sanctioned. Accept keeps the brand
            colour only to mark it as the affirmative choice, not to outweigh
            the other — same shape, same weight, same hit area.
          -->
          <div class="cc-actions">
            <button type="button" mat-flat-button class="cc-reject" (click)="reject()">
              {{ 'shared.cookieConsent.reject' | translate }}
            </button>
            <button type="button" mat-flat-button color="primary" class="cc-accept" (click)="accept()">
              {{ 'shared.cookieConsent.accept' | translate }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .cc-banner {
        position: fixed;
        left: 16px;
        right: 16px;
        bottom: 16px;
        z-index: 10000;
        max-width: 760px;
        margin-inline: auto;
        background: #0f172a;
        color: #e2e8f0;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 16px;
        box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.5);
        padding: 18px 20px;
        animation: cc-up 0.25s ease;
      }

      @keyframes cc-up {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      .cc-inner {
        display: flex;
        gap: 18px;
        align-items: center;
        flex-wrap: wrap;
      }

      .cc-text {
        flex: 1 1 320px;
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: 0.9rem;
        line-height: 1.5;
      }

      .cc-text strong {
        font-weight: 600;
        color: #f8fafc;
      }

      .cc-text span {
        color: #cbd5e1;
      }

      .cc-text a {
        color: #a5b4fc;
        text-decoration: underline;
      }

      .cc-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      /*
        Equal prominence with .cc-accept — same button variant, same metrics,
        only the fill differs so the affirmative choice stays identifiable.
        #f8fafc on the composited surface (#0f172a under 14% white ≈ #2a3444)
        is ~13:1, well past the 4.5:1 floor in DESIGN.md §9.
      */
      .cc-actions button {
        min-width: 132px;
      }

      .cc-reject {
        background: rgba(255, 255, 255, 0.14) !important;
        color: #f8fafc !important;
      }

      .cc-reject:hover {
        background: rgba(255, 255, 255, 0.22) !important;
      }

      @media (max-width: 560px) {
        .cc-inner {
          flex-direction: column;
          align-items: stretch;
        }

        .cc-actions {
          justify-content: stretch;
        }

        .cc-actions button {
          flex: 1;
        }
      }
    `
  ]
})
export class CookieConsentBannerComponent implements OnDestroy {
  private readonly analytics = inject(AnalyticsService);
  private readonly zone = inject(NgZone);

  /** CSS var consumed by `.auth-shell` so the banner never covers the login/register/forgot-password card. */
  private static readonly HEIGHT_VAR = '--vex-cookie-banner-height';
  /** `.cc-banner`'s own `bottom` inset, folded into the published value so the var is the banner's full footprint from the viewport edge — not just its box height. */
  private static readonly BOTTOM_INSET_PX = 16;

  private readonly bannerEl = viewChild<ElementRef<HTMLElement>>('banner');
  private resizeObserver?: ResizeObserver;

  constructor() {
    // Same mechanism as GlobalBannerOffsetDirective (top banner stack), mirrored
    // for this bottom-fixed banner: publish its live height so layouts that
    // need to avoid it can reserve the space instead of being covered by it.
    // Kept local rather than reusing that directive — it hardcodes its own CSS
    // var name, and this is the only bottom-anchored banner in the app.
    effect(() => {
      const el = this.bannerEl()?.nativeElement;
      this.resizeObserver?.disconnect();
      if (!el || typeof ResizeObserver === 'undefined') {
        this.publishHeight(0);
        return;
      }
      this.zone.runOutsideAngular(() => {
        this.resizeObserver = new ResizeObserver(() => this.publishHeight(el.getBoundingClientRect().height));
        this.resizeObserver!.observe(el);
      });
      this.publishHeight(el.getBoundingClientRect().height);
    });
  }

  private publishHeight(height: number): void {
    const footprint = height > 0 ? Math.round(height) + CookieConsentBannerComponent.BOTTOM_INSET_PX : 0;
    document.documentElement.style.setProperty(
      CookieConsentBannerComponent.HEIGHT_VAR,
      `${footprint}px`
    );
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.publishHeight(0);
  }

  /** Marketing privacy policy URL, or null when no marketing origin is set. */
  readonly privacyUrl: string | null = environment.marketingUrl
    ? `${environment.marketingUrl}/privacy`
    : null;

  /**
   * Show only while the decision is still pending, and only in a build where
   * something actually depends on it. `consentRequired` — not `enabled` — is
   * the right gate: GA4 is production-only, but PostHog can be switched on for
   * dev, and in that build the visitor still has to be asked.
   */
  readonly show = computed(() => this.analytics.consentRequired && this.analytics.consent() === null);

  accept(): void {
    this.analytics.accept();
  }

  reject(): void {
    this.analytics.reject();
  }
}
